<template>
  <q-input
    ref="input"
    stack-label
    hide-bottom-space
    :debounce="debounce"
    :model-value="visibleDate"
    :class="{'required-astrx-container': required}"
    :label="label"
    :placeholder="placeholder"
    :outlined="outlined"
    :dense="dense"
    :prefix="prefix"
    :suffix="suffix"
    :hint="hint"
    :readonly="readonly"
    :disable="disable"
    :error="error"
    :clearable="clearable && !mobileMode"
    :error-message="errorMessage"
    @clear="onClearDate"
    @update:model-value="onInputDate"
    @focus="onFocusDate"
    @keydown="onKeydownDate"
    @keyup="onKeyupDate"
  >
    <responsive-menu
      ref="menu"
      :breakpoint="1024"
      :toolbar-title="mobileLabel"
      :no-parent-event="true"
      :no-refocus="true"
      force-on-mobile
    >
      <q-time
        color="white"
        text-color="black"
        :minute-options="[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]"
        :model-value="modelValue"
        class="shadow-0 bg-transparent full-width"
        @update:model-value="(val) => onInputDate(val, true)"
      />
      <q-toolbar>
        <q-btn
          v-if="clearable && !!modelValue"
          size="12px"
          label="Clear"
          flat
          @click="onClearDate"
        />
        <q-space />
        <q-btn
          v-close-popup
          size="12px"
          label="Close"
          flat
        />
      </q-toolbar>
    </responsive-menu>
  </q-input>
</template>

<script>
/**
 * TimePicker acts similar to default QInput component, including props
 */
export default {
  props: {
    modelValue: {
      type: String,
      default: null
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    required: {
      type: Boolean,
      default: false
    },
    prefix: {
      type: String,
      default: undefined
    },
    suffix: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String
    },
    hint: {
      type: String,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    // How long after input should the model value be updated (default 0s - no delay)
    debounce: {
      type: Number,
      default: 0
    },
    // Label to show on mobile devices within the popup
    mobileLabel: {
      type: String,
      default: 'Select Time'
    }
  },
  emits: ['update:modelValue', 'clear'],
  data () {
    return {
      localValue: this.modelValue
    }
  },
  computed: {
    mobileMode () {
      return this.windowWidth <= 1024 || this.$q.platform.is.mobile
    },
    visibleDate () {
      if (!this.modelValue) {
        return null
      }
      const selectedDate = this.getValidatedTime(this.modelValue)
      if (!selectedDate) {
        return null
      }
      return selectedDate.toFormat(this.luxonFormats.TIME)
    }
  },
  watch: {
    'modelValue' (val) {
      this.localValue = val
    }
  },
  methods: {
    getValidatedTime (val) {
      if (!val) {
        return null
      }

      const formats = [this.luxonFormats.TIME, this.luxonFormats.TIME_24HR, 'ha', 'h.mma', 'HH.mm']
      for (const format of formats) {
        const time = this.luxon.fromFormat(val, format)
        if (time.isValid) {
          return time
        }
      }
      return null
    },
    setModelValue () {
      this.$emit('update:modelValue', this.localValue)
      if (!this.localValue) {
        this.$emit('clear')
      }
    },
    /**
     * Main method for checking a time is valid setting the value locally
     * or to the model
     */
    onInputDate (val, updateModel = false) {
      if (this.mobileMode && !updateModel) {
        return
      }

      if (!val) {
        if (this.clearable) {
          this.localValue = null
          if (updateModel) {
            this.setModelValue()
          }
          return
        }
        val = this.luxon.now().toFormat(this.luxonFormats.TIME_24HR)
      }

      val = this.getValidatedTime(val)
      if (!val) {
        return
      }

      val = val.toFormat(this.luxonFormats.TIME_24HR)
      this.localValue = val
      if (updateModel) {
        this.setModelValue()
      }
    },
    /**
     * Clear time
     */
    onClearDate () {
      this.onInputDate(null, true)
    },
    /**
     * Keyup does not work for Tab as it will have focused on a new element.
     * Use keydown to set the local value upon tabbing
     */
    onKeydownDate (e) {
      if (!['Tab'].includes(e.key)) {
        return
      }

      if (e.key === 'Tab') {
        if (this.localValue) {
          this.onInputDate(this.localValue, true)
        }
        this.onBlurDate()
      }
    },
    /**
     * Check the key that was pressed and set the time accordingly
     */
    onKeyupDate (e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
        return
      }

      const date = this.getValidatedTime(this.localValue)
      if (!date) {
        return
      }

      if (e.key === 'ArrowDown') {
        this.onInputDate(date.minus({ minutes: 15 }).toFormat(this.luxonFormats.TIME_24HR), true)
      }
      else if (e.key === 'ArrowUp') {
        this.onInputDate(date.plus({ minutes: 15 }).toFormat(this.luxonFormats.TIME_24HR), true)
      }
      else if (e.key === 'Enter') {
        this.onBlurDate()
        this.$nextTick(() => {
          this.$refs.input.blur()
        })
      }
    },
    onFocusDate () {
      if (this.$refs.menu) {
        this.$refs.menu.open()
      }
    },
    onBlurDate () {
      this.setModelValue()
      if (this.$refs.menu) {
        this.$refs.menu.close()
      }
    },
    focus () {
      this.$refs.input.focus()
    }
  }
}
</script>
